import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, Field, Loading, ImageUpload } from "@/components/ui-helpers";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Store, Copy, ExternalLink, Pencil, Lock } from "lucide-react";
import { toast } from "sonner";

const PLACEHOLDERS = ["[Product Name]", "[Trip Name]", "[Variant]", "[Final Price]", "[Quantity]", "[Product URL]"];
const DEFAULT_BANNER = "https://images.unsplash.com/photo-1523059623039-a9ed027e7fad?q=80&w=1600&auto=format&fit=crop";

export default function Catalogue() {
  const { trips } = useApp();
  const { isAdmin } = useAuth();
  const [settingsMap, setSettingsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [editTrip, setEditTrip] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const entries = await Promise.all(trips.map(async (t) => {
        try { const r = await api.get(`/catalogue-settings/${t.id}`); return [t.id, r.data]; }
        catch { return [t.id, null]; }
      }));
      setSettingsMap(Object.fromEntries(entries));
    } finally { setLoading(false); }
  }, [trips]);
  useEffect(() => { if (trips.length) load(); else setLoading(false); }, [load, trips.length]);

  const slugOrId = (t) => settingsMap[t.id]?.catalogue_slug || t.id;
  const linkFor = (t) => `${window.location.origin}/katalog/${slugOrId(t)}`;
  const copy = (t) => { navigator.clipboard.writeText(linkFor(t)); toast.success("Link katalog disalin"); };

  const openEdit = (t) => {
    const s = settingsMap[t.id] || {};
    setEditTrip(t);
    setForm({
      catalogue_title: s.catalogue_title || t.name,
      catalogue_description: s.catalogue_description || "",
      whatsapp_number: s.whatsapp_number || "",
      whatsapp_message_template: s.whatsapp_message_template || "",
      catalogue_status: s.catalogue_status || "active",
      catalogue_slug: s.catalogue_slug || "",
      order_closing_date: (s.order_closing_date || t.order_closing_date || "").slice(0, 10),
      show_unavailable: !!s.show_unavailable,
      hero_image: s.hero_image || "",
      hero_opacity: typeof s.hero_opacity === "number" ? s.hero_opacity : 0.9,
    });
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    const num = (form.whatsapp_number || "").replace(/[^0-9]/g, "");
    if (num.length < 8 || num.length > 15) return toast.error("Nomor WhatsApp tidak valid. Contoh: 6281234567890");
    setSaving(true);
    try {
      await api.put(`/catalogue-settings/${editTrip.id}`, { ...form, whatsapp_number: num });
      toast.success("Pengaturan katalog disimpan");
      setEditTrip(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan"); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Katalog Publik" subtitle="Bagikan katalog produk ke pelanggan tanpa menampilkan data internal" />
      {loading ? <Loading /> : trips.length === 0 ? <EmptyState title="Belum ada trip" subtitle="Buat trip dan produk untuk membuat katalog." /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {trips.map((t) => {
            const s = settingsMap[t.id] || {};
            return (
              <div key={t.id} className="rounded-xl border border-border bg-card p-5 shadow-sm" data-testid="catalogue-card">
                <div className="flex items-start justify-between">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center"><Store className="h-5 w-5 text-primary" /></div>
                  <div className="flex items-center gap-1.5">
                    <StatusBadge status={s.catalogue_status === "inactive" ? "Cancelled" : "Open for Orders"} />
                  </div>
                </div>
                <p className="font-heading font-semibold mt-3">{s.catalogue_title || t.name}</p>
                <p className="text-xs text-muted-foreground">{t.code}</p>
                <div className="mt-2 space-y-1 text-xs">
                  <p className="text-muted-foreground">WhatsApp: <span className="font-mono text-foreground">{s.whatsapp_number || "-"}</span></p>
                  <p className="text-muted-foreground">Status: <span className="text-foreground">{s.catalogue_status === "inactive" ? "Nonaktif" : "Aktif"}</span></p>
                </div>
                <div className="mt-3 rounded-lg bg-secondary/40 px-3 py-2 text-xs font-mono truncate">{linkFor(t)}</div>
                <div className="flex gap-2 mt-3">
                  {isAdmin ? (
                    <Button size="sm" variant="outline" onClick={() => openEdit(t)} data-testid="catalogue-edit-button"><Pencil className="h-3.5 w-3.5 mr-1" />Edit</Button>
                  ) : (
                    <Button size="sm" variant="outline" disabled title="Khusus admin"><Lock className="h-3.5 w-3.5 mr-1" />Edit</Button>
                  )}
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => copy(t)} data-testid="catalogue-copy-link"><Copy className="h-3.5 w-3.5 mr-1" />Copy Link</Button>
                  <Button size="sm" variant="outline" onClick={() => window.open(linkFor(t), "_blank")} data-testid="catalogue-open"><ExternalLink className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!editTrip} onOpenChange={() => setEditTrip(null)}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Katalog · {editTrip?.name}</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-4">
              <Field label="Judul Katalog"><Input value={form.catalogue_title} onChange={(e) => set("catalogue_title", e.target.value)} data-testid="cat-title-input" /></Field>
              <Field label="Deskripsi Katalog"><Textarea value={form.catalogue_description} onChange={(e) => set("catalogue_description", e.target.value)} data-testid="cat-desc-input" /></Field>

              {/* Banner Katalog */}
              <div className="rounded-xl border border-border p-4 space-y-3" data-testid="cat-banner-section">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">Banner Katalog</p>
                  <span className="text-xs text-muted-foreground">Tampilan atas katalog publik</span>
                </div>
                {/* Live preview */}
                <div className="overflow-hidden rounded-lg border border-border">
                  <div className="relative h-28">
                    <img src={form.hero_image || DEFAULT_BANNER} alt="" className="absolute inset-0 h-full w-full object-cover"
                      style={{ opacity: form.hero_opacity }} onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
                    <div className="absolute inset-0" style={{ backgroundImage: "linear-gradient(96deg, hsla(38,50%,96%,0.98) 0%, hsla(38,50%,96%,0.86) 42%, hsla(38,50%,96%,0.42) 100%)" }} />
                    <div className="relative p-3">
                      <p className="text-sm font-semibold" style={{ color: "hsl(180 24% 10%)", fontFamily: "'Fraunces', serif" }}>{form.catalogue_title || editTrip?.name}</p>
                      <p className="text-[11px] mt-0.5" style={{ color: "hsl(180 8% 38%)" }}>{form.catalogue_description || "Deskripsi katalog"}</p>
                    </div>
                  </div>
                </div>
                <Field label="URL Gambar Banner" hint="Tempel URL gambar, atau unggah di bawah. Kosongkan untuk pakai default Sydney.">
                  <Input value={form.hero_image} onChange={(e) => set("hero_image", e.target.value)} data-testid="cat-banner-url" placeholder="https://…/banner.jpg" />
                </Field>
                <ImageUpload value={form.hero_image} onChange={(v) => set("hero_image", v || "")} label="Banner" testId="cat-banner-upload" />
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm text-muted-foreground">Ketebalan banner (fade)</span>
                    <span className="text-sm font-medium tabular-nums" data-testid="cat-banner-opacity-value">{Math.round((form.hero_opacity ?? 0.9) * 100)}%</span>
                  </div>
                  <Slider value={[form.hero_opacity ?? 0.9]} min={0.1} max={1} step={0.05}
                    onValueChange={(v) => set("hero_opacity", v[0])} data-testid="cat-banner-opacity" />
                  <p className="text-[11px] text-muted-foreground mt-1.5">Geser ke kiri agar banner lebih pudar dan judul makin jelas.</p>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Nomor WhatsApp" required hint="Format internasional tanpa + / spasi. Cth: 6281234567890">
                  <Input value={form.whatsapp_number} onChange={(e) => set("whatsapp_number", e.target.value)} data-testid="cat-wa-input" placeholder="6281234567890" />
                </Field>
                <Field label="Slug URL Katalog" hint="huruf/angka/strip">
                  <Input value={form.catalogue_slug} onChange={(e) => set("catalogue_slug", e.target.value)} data-testid="cat-slug-input" placeholder="sydney-agustus" />
                </Field>
                <Field label="Status Katalog">
                  <Select value={form.catalogue_status} onValueChange={(v) => set("catalogue_status", v)}>
                    <SelectTrigger data-testid="cat-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="active">Aktif</SelectItem><SelectItem value="inactive">Nonaktif</SelectItem></SelectContent>
                  </Select>
                </Field>
                <Field label="Tgl Tutup Order"><Input type="date" value={form.order_closing_date || ""} onChange={(e) => set("order_closing_date", e.target.value)} data-testid="cat-closing-input" /></Field>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.show_unavailable} onChange={(e) => set("show_unavailable", e.target.checked)} data-testid="cat-show-unavailable" />
                Tampilkan produk yang tidak tersedia
              </label>
              <Field label="Pesan WhatsApp Default" hint="Placeholder tersedia (klik untuk menyisipkan)">
                <Textarea rows={7} value={form.whatsapp_message_template} onChange={(e) => set("whatsapp_message_template", e.target.value)} data-testid="cat-template-input" className="font-mono text-xs" />
              </Field>
              <div className="flex flex-wrap gap-1.5">
                {PLACEHOLDERS.map((p) => (
                  <button key={p} type="button" onClick={() => set("whatsapp_message_template", (form.whatsapp_message_template || "") + " " + p)}
                    className="text-[11px] rounded-full border border-border bg-secondary/60 px-2 py-1 hover:bg-secondary" data-testid="cat-placeholder-chip">{p}</button>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTrip(null)}>Batal</Button>
            <Button onClick={save} disabled={saving} data-testid="cat-save-button">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
